import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent } from '@material-ui/core';
import { DeletePatient, ProgramPatients, IPaginated, GetProgramPatientsPaged } from '@bforms-web/common';
import { useLoading } from '../../../contexts/loading.context';
import PatientTable from '../../PatientTable';
import { useStyles } from './styles';
import { toast } from 'react-toastify';
import { KeysLocalStorage } from '../../../enums/keys-localstorage';
import { PATIENTS_ROUTE } from '../../../constants';
import { useHistory } from 'react-router-dom';
import SearchIcon from '@material-ui/icons/Search';

export type ProgramPatientsListProps = {
  getProgramPatientsPaged: GetProgramPatientsPaged;
  deletePatient: DeletePatient,
};

type ProgramPatientsListState = {
  programPatients: IPaginated<ProgramPatients>;
};

type SortOrder = 'name-asc' | 'name-desc' | 'date-asc' | 'date-desc';

type FilterState = {
  page: number;
  rows: number;
  timeout: number;
  search: string;
  sortOrder: SortOrder;
};

const defaultState: ProgramPatientsListState = {
  programPatients: {
    data: [],
    total: 0,
  }
};

const defaultFilterState: FilterState = {
  page: 0,
  rows: 10,
  timeout: 0,
  search: '',
  sortOrder: 'name-asc',
};

export default function PatientList({ getProgramPatientsPaged, deletePatient }: ProgramPatientsListProps): JSX.Element {
  const classes = useStyles();
  const [state, setState] = useState<ProgramPatientsListState>(defaultState);
  const [filterState, setFilterState] = useState<FilterState>(defaultFilterState);
  const [searchInput, setSearchInput] = useState('');
  const { showLoading, hideLoading } = useLoading();
  const { push } = useHistory();

  const getServerOrderParams = useCallback((sortOrder: string) => {
    const [field, direction] = sortOrder.split('-') as [string, 'asc' | 'desc'];
    
    const fieldMap: Record<string, string> = {
      'name': 'patientName',
      'date': 'createdAt'
    };
    
    return {
      orderBy: fieldMap[field] || 'patientName',
      orderDirection: direction || 'asc'
    };
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      (async () => {
        try {
          localStorage.removeItem(KeysLocalStorage.DATA_PATIENT);
          
          const { orderBy, orderDirection } = getServerOrderParams(filterState.sortOrder);
          
          const programPatients = await getProgramPatientsPaged.execute({
            programId: 'NoProgram',
            filter: filterState.search || 'NoFilter',
            skip: filterState.page * filterState.rows,
            take: filterState.rows,
            orderBy,
            orderDirection
          });

          setState({
            programPatients: {
              ...programPatients,
              data: programPatients.data,
              total: programPatients.total
            },
          });
        } catch (err) {
          console.error('Erro ao carregar pacientes:', err);
          toast.error('Ocorreu um erro ao carregar a lista de pacientes.');
        } finally {
          hideLoading();
        }
    })();
    }, filterState.timeout);
    return () => clearTimeout(handler);
  }, [filterState, getProgramPatientsPaged, hideLoading, showLoading, getServerOrderParams]);

  const handlePageChange = useCallback((event: unknown, page: number) => {
    setFilterState(prev => ({
      ...prev,
      page,
      timeout: 200,
    }));
  }, []);

  const handleRowsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10) || 10;
    setFilterState((prevFilterState) => ({
      ...prevFilterState,
      rows: newRows,
      page: 0,
    }));
  }, []);
  const handleRemoveClick = async (programPatient: ProgramPatients) => {
    showLoading();
    try {
      // Verifica se as propriedades necessárias estão disponíveis
      if (!programPatient.patient?.id || !programPatient.program?.id) {
        throw new Error('Dados do paciente ou programa não encontrados');
      }

      await deletePatient.execute({
        patientId: programPatient.patient.id,
        programId: programPatient.program.id
      });

      const { orderBy, orderDirection } = getServerOrderParams(filterState.sortOrder);
      
      const programPatients = await getProgramPatientsPaged.execute({
        programId: 'NoProgram',
        filter: filterState.search || 'NoFilter',
        skip: filterState.page * filterState.rows,
        take: filterState.rows,
        orderBy,
        orderDirection
      });
      
      setState({
        programPatients: {
          ...programPatients,
          data: programPatients.data,
          total: programPatients.total
        },
      });

      toast.success('Paciente removido com sucesso.');
    } catch (err) {
      console.error('Erro ao remover paciente:', err);
      toast.error(err.message || 'Ocorreu um erro ao remover o paciente.');
    } finally {
      hideLoading();
    }
  };
  
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    
    setFilterState(prev => ({
      ...prev,
      search: value,
      page: 0,
      timeout: 500,
    }));
  }, []);

  const handleSortChange = useCallback((event: SelectChangeEvent<SortOrder>) => {
    setFilterState(prev => ({
      ...prev,
      sortOrder: event.target.value as SortOrder,
      page: 0,
    }));
  }, []);

  return (
    <Box className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Buscar pacientes..."
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: 300 }}
        />
        <FormControl variant="outlined" style={{ minWidth: 200 }}>
          <InputLabel id="sort-order-label">Ordenar por</InputLabel>
          <Select
            labelId="sort-order-label"
            value={filterState.sortOrder}
            onChange={handleSortChange}
            label="Ordenar por"
          >
            <MenuItem value="name-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="name-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="date-desc">Data (mais recente)</MenuItem>
            <MenuItem value="date-asc">Data (mais antigo)</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <PatientTable
        data={state.programPatients.data}
        count={state.programPatients.total}
        page={filterState.page}
        rowsPerPage={filterState.rows}
        onChangePage={handlePageChange}
        onChangeRows={handleRowsChange}
        onRemove={handleRemoveClick}
      />
    </Box>
  );
}